import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
// @mui
import { styled } from '@mui/material/styles';
import { Box, Stack, AppBar, Toolbar, Typography, IconButton, Collapse, Tooltip } from '@mui/material';
// hooks
import useAuth from '../../../hooks/useAuth';
import useOffSetTop from '../../../hooks/useOffSetTop';
import useResponsive from '../../../hooks/useResponsive';
// Redux Hooks e Actions
import { useDispatch, useSelector } from '../../../redux/store';
import { toggleStatsPanel } from '../../../redux/slices/uiConfigSlice';
// utils
import cssStyles from '../../../utils/cssStyles';
// config
import { HEADER, NAVBAR } from '../../../config';
// components
import Logo from '../../../components/Logo';
import Iconify from '../../../components/Iconify';
import { IconButtonAnimate } from '../../../components/animate';
import FixedStatsPanel from '../../../components/StatsPanel';
//
import AccountPopover from './AccountPopover';
import PauseSet from './PauseSet';

// ----------------------------------------------------------------------

// Componente SVG Customizado
const CustomStatsIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth="1.5"
    {...props}
  >
    <g fill="none">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M10 9H6m9.5 2a2.5 2.5 0 1 1 0-5a2.5 2.5 0 0 1 0 5M6 6h3m9 12l-4.5-3l-2.5 2l-5-4"
      />
      <path d="M3 20.4V3.6a.6.6 0 0 1 .6-.6h16.8a.6.6 0 0 1 .6.6v16.8a.6.6 0 0 1-.6.6H3.6a.6.6 0 0 1-.6-.6Z" />
    </g>
  </svg>
);

const PORT = window.location.hostname === 'localhost' ? '8080' : '8443';

const getUiConfig = (state) => state.uiConfig;

const RootStyle = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'isCollapse' && prop !== 'isOffset' && prop !== 'verticalLayout',
})(({ isCollapse, isOffset, verticalLayout, theme }) => ({
  ...cssStyles(theme).bgBlur(),
  boxShadow: 'none',
  minHeight: HEADER.MOBILE_HEIGHT,
  zIndex: theme.zIndex.appBar + 1,
  transition: theme.transitions.create(['width', 'min-height'], {
    duration: theme.transitions.duration.shorter,
  }),
  [theme.breakpoints.up('lg')]: {
    minHeight: HEADER.DASHBOARD_DESKTOP_HEIGHT,
    width: `calc(100% - ${NAVBAR.DASHBOARD_WIDTH + 1}px)`,
    ...(isCollapse && {
      width: `calc(100% - ${NAVBAR.DASHBOARD_COLLAPSE_WIDTH}px)`,
    }),
    ...(isOffset && {
      minHeight: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT,
    }),
    ...(verticalLayout && {
      width: '100%',
      minHeight: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT,
      backgroundColor: theme.palette.background.default,
    }),
  },
}));

// ----------------------------------------------------------------------

DashboardHeader.propTypes = {
  onOpenSidebar: PropTypes.func,
  isCollapse: PropTypes.bool,
  verticalLayout: PropTypes.bool,
};

export default function DashboardHeader({ onOpenSidebar, isCollapse = false, verticalLayout = false }) {
  const isOffset = useOffSetTop(HEADER.DASHBOARD_DESKTOP_HEIGHT) && !verticalLayout;

  const isDesktop = useResponsive('up', 'lg');
  const { user } = useAuth();
  const [imageTimestamp, setImageTimestamp] = useState(Date.now());
  const imageUrl = `${window.location.protocol}//${window.location.hostname}:${PORT}/external-api/company-logo/${user?.company?._id}?t=${imageTimestamp}`;

  const dispatch = useDispatch();
  const { showStatsPanel } = useSelector(getUiConfig);

  const handleTogglePanel = () => {
    dispatch(toggleStatsPanel());
  };

  const hasPermissionForStats =
    user.acl.profile === 'master' ||
    user.acl.profile === 'admin' ||
    user.acl.profile === 'supervisor' ||
    user.acl.profile === 'superadmin';

  useEffect(() => {
    const handleLogoUpdate = () => {
      setImageTimestamp(Date.now());
    };
    window.addEventListener('company-logo-updated', handleLogoUpdate);

    return () => {
      window.removeEventListener('company-logo-updated', handleLogoUpdate);
    };
  }, []);

  return (
    <RootStyle isCollapse={isCollapse} isOffset={isOffset} verticalLayout={verticalLayout}>
      <Toolbar
        sx={{
          minHeight: '100% !important',
          px: { lg: 5 },
        }}
      >
        {isDesktop && verticalLayout && <Logo sx={{ mr: 2.5 }} />}
        <object data={imageUrl} type="image/png" height={40}>
          <img src="/images/image-preview.png" alt={user?.company?.name || 'Logo'} height={40} />
        </object>
        <Typography variant="h6" color="#000" sx={{ ml: 2 }}>
          {user?.company?.name}
        </Typography>

        {!isDesktop && (
          <IconButtonAnimate onClick={onOpenSidebar} sx={{ mr: 1, color: 'text.primary' }}>
            <Iconify icon="eva:menu-2-fill" />
          </IconButtonAnimate>
        )}
        <Box sx={{ flexGrow: 1 }} />

        <Stack direction="row" alignItems="center" spacing={{ xs: 0.5, sm: 1.5 }}>
          {/* Controles do Painel de Estatísticas (Apenas se tiver permissão) */}
          {hasPermissionForStats && (
            <Tooltip title="Informações de Atendimento">
              <IconButton // O IconButton que controla o toggle
                onClick={handleTogglePanel}
                sx={{
                  backgroundColor: showStatsPanel ? 'primary.lighter' : 'transparent',
                  '&:hover': {
                    backgroundColor: showStatsPanel ? 'primary.light' : 'action.hover',
                  },
                  p: 1,
                  ml: 1,
                  color: 'text.secondary',
                }}
              >
                <CustomStatsIcon width={28} height={28} />
              </IconButton>
            </Tooltip>
          )}
          {user.acl.profile !== 'master' && <PauseSet />}
          <AccountPopover />
        </Stack>
      </Toolbar>
      {/* PAINEL DE ESTATÍSTICAS ABAIXO DA TOOLBAR */}
      {hasPermissionForStats && (
        <Collapse in={showStatsPanel} timeout={300}>
          <Box
            sx={{
              px: { lg: 5 },
              pt: 1,
              pb: 2,
            }}
          >
            <FixedStatsPanel />
          </Box>
        </Collapse>
      )}
    </RootStyle>
  );
}
